import Sort, {SortType} from "./catalog-list/Sort";
import FilterTags from "./catalog-list/FilterTags";
import Subscribe from "../core/Subscribe";
import ModelListCard from "./ModelListCard";
import Breadcrumbs from "../core/Breadcrumbs";
import {useEffect, useState} from "react";
import ModelColorItemInterface from "../../interfaces/model/ModelColorItemInterface";
import {objclone} from "../../utils/Utils";
import PageMetaInterface from "../../interfaces/core/PageMetaInterface";
import ModelSuperCategoryInterface from "../../interfaces/model/ModelSuperCategoryInterface";
import BreadcrumbInterface from "../../interfaces/core/BreadcrumbInterface";
import BannerInterface from "../../interfaces/catalog/BannerInterface";
import {FilterSuperCategoryInterface} from "../../interfaces/catalog/FilterSuperCategoryInterface";
import Filters from "./catalog-list/Filters";
import {SelectedFiltersInterface} from "../../interfaces/catalog/SelectedFiltersInterface";
import Pagination from "../core/Pagination";
import BottomTags from "../core/BottomTags";
import TagInterface from "../../interfaces/core/TagInterface";
import {useLocation, useSearchParams} from "react-router-dom";
import useDatalayer, {EcommerceAction} from "../../utils/useDataLayer";
import Header from './catalog-list/Header'

export default function CatalogList({modelColorItems, meta, category, breadcrumbs, banners, tags, superCategories}: {
    modelColorItems: ModelColorItemInterface[],
    meta: PageMetaInterface | undefined,
    category: ModelSuperCategoryInterface | undefined,
    breadcrumbs: BreadcrumbInterface[] | undefined,
    banners: BannerInterface[] | undefined,
    tags: TagInterface[] | undefined,
    superCategories: FilterSuperCategoryInterface[]
}) {

    const defaultFilterState: SelectedFiltersInterface = {
        colors: [],
        sizes: [],
        superCategories: [],
        priceRange: null
    };

    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const [isFiltersOpen, setIsFilterOpen] = useState(false);
    const [page, setPage] = useState(Number(searchParams.get('page')) || 1);
    const [additionalPages, setAdditionalPages] = useState(0);
    const [modelsPerPage, setModelsPerPage] = useState(36);
    const [selectedFilters, setSelectedFilters] = useState<SelectedFiltersInterface>(defaultFilterState);
    const [sortBy, setSortBy] = useState(SortType.RAITING);
    const [selectedTags, setSelectedTags] = useState<TagInterface[]>([])

    const {category: dataLayerCategory} = useDatalayer()

    useEffect(() => {
        if (additionalPages) return
        // @ts-ignore
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        })
    }, [page]);

    const getBaseUrl = () => {
        return location.pathname
    }


    const getFilteredAndSortedModelColorItems = (): ModelColorItemInterface[] => {
        let modelColorItemsFilterd: ModelColorItemInterface[] = objclone(modelColorItems)

        if (selectedFilters.colors.length) {
            modelColorItemsFilterd = modelColorItemsFilterd.filter(colorItem => selectedFilters.colors.map(color => color.color).indexOf(colorItem.color_human_name) > -1)
        }
        if (selectedFilters.sizes.length) {
            modelColorItemsFilterd = modelColorItemsFilterd.filter(colorItem => {
                let flag = false
                selectedFilters.sizes.map(size => {
                    if (colorItem.stocks[size] && (colorItem.stocks[size][2] || colorItem.stocks[size][4])) {
                        flag = true
                    }
                })
                return flag
            })
        }

        if (selectedFilters.superCategories.length) {
            const selectedSuperCategoryId = selectedFilters.superCategories.map(superCategory => superCategory.id).flat()
            modelColorItemsFilterd = modelColorItemsFilterd.filter(colorItem => selectedSuperCategoryId.includes(colorItem.super_category_id))
        }

        if (selectedFilters.priceRange?.to) {
            const priceFrom = selectedFilters.priceRange.from;
            const priceTo = selectedFilters.priceRange.to;
            modelColorItemsFilterd = modelColorItemsFilterd.filter(colorItem => {
                return colorItem.min_price.amount / 100 >= priceFrom && colorItem.max_price.amount / 100 <= priceTo
            })
        }

        if (selectedTags.length) {
            modelColorItemsFilterd = modelColorItemsFilterd.filter(colorItem => {
                let pass = false
                selectedTags.map(tag => {
                    if (colorItem.tag_relations.indexOf(tag.id) !== -1) {
                        pass = true
                    }
                })
                return pass
            })
        }

        modelColorItemsFilterd.sort((a, b) => {
            switch (sortBy) {
                case SortType.RAITING:
                    if (a.score === b.score) return 0;
                    return a.score < b.score ? 1 : -1;
                case SortType.DISCOUNT:
                    if (a.discount === b.discount) return 0;
                    return a.discount < b.discount ? 1 : -1;
                case SortType.PRICE_ASC:
                    if (a.min_price.amount === b.min_price.amount) return 0;
                    return a.min_price.amount < b.min_price.amount ? 1 : -1;
                case SortType.PRICE_DESC:
                    if (a.min_price.amount === b.min_price.amount) return 0;
                    return a.min_price.amount > b.min_price.amount ? 1 : -1;
                case SortType.NEW:
                default:
                    if (a.score === b.score) return 0;
                    return a.score < b.score ? 1 : -1;
            }
        });

        return modelColorItemsFilterd
    }


    const getModelsOnPage = () => {
        return getFilteredAndSortedModelColorItems().slice((page - 1 - additionalPages) * modelsPerPage, page * modelsPerPage);
    }

    const getFilterCount = (): number => {
        let filterQuantity = (selectedFilters.priceRange?.from || selectedFilters.priceRange?.to) ? 1 : 0
        filterQuantity += selectedFilters.colors.length
        filterQuantity += selectedFilters.sizes.length
        return filterQuantity
    }

    useEffect(() => {
        if (selectedFilters.colors.length || selectedFilters.sizes.length || selectedFilters.superCategories.length || selectedFilters.priceRange?.from || selectedFilters.priceRange?.to || selectedTags.length) {
            setSearchParams({})
        }
        setAdditionalPages(0)
    }, [selectedFilters, selectedTags, sortBy]);

    useEffect(() => {
        setSearchParams({})
        setAdditionalPages(0)
    }, [sortBy]);

    useEffect(() => {
        setPage(Number(searchParams.get('page')) || 1)
        setAdditionalPages(0)
    }, [location.pathname, searchParams.get('page')]);

    useEffect(() => {
        if(modelColorItems?.length){
            dataLayerCategory(EcommerceAction.VIEW_CATEGORY, modelColorItems[0].category_uid)
        }
    }, [modelColorItems]);

    const totalPages = Math.ceil(getFilteredAndSortedModelColorItems().length / modelsPerPage)

    return (<>
        {<Filters isOpen={isFiltersOpen} setIsOpen={setIsFilterOpen} modelColorItems={modelColorItems}
                  superCategories={superCategories} setSelectedFilters={setSelectedFilters}/>}

        <main className="main-catalog">
            <section className="catalog">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col">
                            <Breadcrumbs breadcrumbs={breadcrumbs || []}/>
                            {meta && <Header meta={meta} count={modelColorItems.length} />}
                            <div className="filters">
                                <div className="filters__wrap">
                                    <div className="filters__list">
                                        <div className="filters__list-first">
                                            <a className="filters__list-link filters__list-link--all f-exp" href="/"
                                               onClick={(event) => {
                                                   event.preventDefault()
                                                   setIsFilterOpen(!isFiltersOpen)
                                               }}><i
                                                className="ico ico-burger-equalizer ico-left"></i><span
                                                className="desk">Все фильтры</span><sup
                                                className="numbers">{getFilterCount()}</sup></a>
                                        </div>
                                        <FilterTags tags={(tags || []).filter(tag => tag.tag_position === 0)}
                                                    selectedTags={selectedTags} setSelectedTags={setSelectedTags}/>
                                        <Sort sortBy={sortBy} setSortBy={setSortBy}/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="catalog__list row">
                        {getModelsOnPage().map((modelColorItem, index) => <div key={index}
                                                                               className={("col-lg-3 ") + (index > 3 && (index % 3 === 0) ? ' col-12' : ' col-6')}>
                            <ModelListCard modelColorItem={modelColorItem}/>
                        </div>)}
                        {/*{getModelsOnPage().map((modelColorItem, index) => <div key={index} className={ ( index >= 10 && index % 10 === 0 ? "col-lg-6 " :  "col-lg-3 ") + (index > 3 && (index % 3 === 0) ? ' col-12' : ' col-6')}>*/}
                        {/*    { (index < 10 || index % 10 != 0) && <ModelListCard modelColorItem={modelColorItem}/>}*/}
                        {/*    { (banners && index >= 10 && index % 10 === 0) && <Banner banner={banners[0]}/>}*/}
                        {/*</div>)}*/}
                    </div>
                    {totalPages > 1 && <div className="row">
                        <div className="col">
                            <Pagination page={page} setPage={setPage}
                                        totalPages={totalPages}
                                        additionalPages={additionalPages}
                                        setAdditionalPages={setAdditionalPages}
                                        baseUrl={getBaseUrl()}
                            />
                        </div>
                    </div>}
                </div>
            </section>
            <section className="information">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-8 offset-lg-2">
                            <div className="information__collapse">
                                <BottomTags tags={(tags || []).filter(tag => tag.tag_position === 1)}
                                            selectedTags={selectedTags} setSelectedTags={setSelectedTags}
                                            singleMode={false}/>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <Subscribe/>
        </main>
    </>)
}